import { useGermanEnergyInfrastructure } from "../lib/useGermanEnergyInfrastructure";
import { find21hRuleForLayer } from "../lib/allgemeinerlaubnis";
import { supplementalInfrastructureAsGeozones } from "@client/lib/energyInfrastructureZones";
import type {
  GermanEnergyInfrastructureItem,
  InfrastructureSourceStatus,
} from "@shared/germanEnergyInfrastructure";
import {
  getEnergyInfrastructureCountry,
  hasIncompleteEnergyInfrastructureSources,
  getRadarEnergyInfrastructureItems,
} from "@shared/germanEnergyInfrastructure";

function formatDistance(meters: number): string {
  return meters < 1000 ? `${Math.round(meters)} m` : `${(meters / 1000).toFixed(1)} km`;
}

function sourceStatusLabel(source: InfrastructureSourceStatus): string {
  switch (source.status) {
    case "available": return "geladen";
    case "stale": return "älterer Cache";
    case "partial": return "teilweise";
    default: return "nicht erreichbar";
  }
}

function kindLabel(item: GermanEnergyInfrastructureItem): string {
  switch (item.kind) {
    case "wind_turbine": return "Windkraftanlage";
    case "power_line": return "Stromleitung";
    case "power_mast": return "Strommast";
  }
}

function comparisonLabel(
  item: GermanEnergyInfrastructureItem,
  country: NonNullable<ReturnType<typeof getEnergyInfrastructureCountry>>,
): string {
  if (item.source === "RIVM" || item.source === "BFE" || item.source === "ACT") {
    return "Amtlicher Anlagendatensatz; Flugbeschränkung nicht abgeglichen";
  }
  const source = country.assetComparisonSourceByKind?.[item.kind] ??
    country.officialGeozoneSource.label;
  if (item.officialMatch === "not_found") return `Nicht in ${source} bestätigt – Zusatzdaten`;
  if (item.officialMatch === "unknown") {
    return country.comparableOfficialAssetKinds.includes(item.kind)
      ? `Kein sicherer Abgleich mit ${source} möglich`
      : "Für diesen Anlagentyp ist kein amtlicher Vergleichsdatensatz eingebunden";
  }
  return item.kind === "power_mast"
    ? `${source}-Stromleitung in der Nähe`
    : `${source}-Treffer in der Nähe`;
}

function safeWebsiteHref(value: string | null | undefined): string | null {
  if (!value?.trim()) return null;
  try {
    const candidate = /^[a-z][a-z\d+.-]*:/i.test(value) ? value : `https://${value}`;
    const url = new URL(candidate);
    return url.protocol === "http:" || url.protocol === "https:" ? url.href : null;
  } catch {
    return null;
  }
}

export default function GermanEnergyInfrastructurePanel({
  countryIso,
  latitude,
  longitude,
  radiusKm = 5,
}: {
  countryIso: string;
  latitude: number;
  longitude: number;
  radiusKm?: number;
}) {
  const searchRadiusKm = Math.min(5, Math.max(0.5, radiusKm));
  const { data, isLoading, error } = useGermanEnergyInfrastructure(
    latitude,
    longitude,
    searchRadiusKm,
    countryIso,
  );
  const country = data?.country ?? getEnergyInfrastructureCountry(countryIso);
  const officialSourceLabel =
    country?.assetComparisonSourceLabel ??
    country?.officialGeozoneSource.label ??
    "amtliche Geozonen";
  const verified = getRadarEnergyInfrastructureItems(true, data?.features, data?.sources, country?.assetComparisonStatusKeyByKind);
  const sourceItems = countryIso === "DE"
    ? [...(data?.features.filter((item) => item.source === "MaStR") ?? []), ...verified.filter((item) => item.source !== "MaStR")]
    : verified;
  const zones = supplementalInfrastructureAsGeozones(countryIso, sourceItems);
  const presentedItems = zones.flatMap((zone) => {
    const item = sourceItems.find((record) => zone.featureId?.endsWith(`:${record.id}`));
    return item ? [{ ...item, displayedSource: zone.details.split("\n").find((line) => line.startsWith("Quelle:"))?.slice(7).trim() ?? item.source }] : [];
  });
  const foundCount = presentedItems.length;
  const visibleItems = presentedItems.slice(0, 10);
  const omittedCount = Math.max(0, foundCount - visibleItems.length);
  const energyRule = countryIso === "DE" ? find21hRuleForLayer("Windkraftanlagen") : null;
  const sourcesIncomplete = data
    ? !!error || hasIncompleteEnergyInfrastructureSources(data)
    : false;

  return (
    <section
      className="rounded-lg border border-[var(--color-app-border)] bg-[var(--color-app-bg)] p-3 space-y-3"
      data-testid="german-energy-infrastructure"
    >
      <div>
        <h3 className="text-sm font-semibold text-[var(--color-app-text)]">
          Geozonen und Infrastruktur
        </h3>
      </div>

      {isLoading ? (
        <p className="text-xs text-[var(--color-app-text-secondary)]">
          Anlagenquellen und {officialSourceLabel} werden abgefragt …
        </p>
      ) : error && !data ? (
        <p className="text-xs text-[var(--color-caution)]">
          Zusatzdaten konnten nicht geladen werden. Daraus lässt sich nicht schließen, dass keine Anlagen vorhanden sind.
        </p>
      ) : data ? (
        <>
          {error ? (
            <p className="text-xs text-[var(--color-caution)]">
              Die Zusatzquellen konnten nicht aktualisiert werden. Die zuletzt geladenen Treffer bleiben sichtbar; ein fehlender Treffer ist nicht verlässlich.
            </p>
          ) : null}

          {sourcesIncomplete ? (
            <p className="text-xs text-[var(--color-caution)]">
              Teilergebnis: Mindestens eine Zusatzquelle ist veraltet, unvollständig oder nicht erreichbar. Auch bei 0 Treffern lässt sich daraus nicht auf Anlagenfreiheit schließen.
            </p>
          ) : null}

          {foundCount === 0 ? (
            <p className="text-xs text-[var(--color-app-text-secondary)]">
              {sourcesIncomplete
                ? "In den verfügbaren Zusatzdaten gibt es derzeit keinen Treffer."
                : "In den abgefragten Zusatzdaten gibt es derzeit keinen Treffer."} Das ist keine Garantie für Anlagenfreiheit.
            </p>
          ) : (
            <div className="space-y-2">
              {visibleItems.map((item) => {
                const caution = countryIso === "DE" && item.distanceMeters < 100;
                return (
                  <article
                    key={item.id}
                    className="rounded-md border border-[var(--color-app-border)] bg-[var(--color-app-bg)] p-2"
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <p className="text-sm font-medium text-[var(--color-app-text)]">{item.name}</p>
                        <p className="text-xs text-[var(--color-app-text-secondary)]">
                          {kindLabel(item)} · {formatDistance(item.distanceMeters)} vom Start
                        </p>
                      </div>
                      <span className="shrink-0 rounded bg-blue-100 text-blue-800 px-1.5 py-0.5 text-[10px] font-semibold">
                        {kindLabel(item)}
                      </span>
                    </div>
                    <p className="mt-1 text-xs text-[var(--color-app-text-secondary)]">
                      Betreiber: {item.operatorName || item.operatorMastrNumber || "nicht angegeben"}
                    </p>
                    {item.operatorMastrNumber ? (
                      <p className="text-xs text-[var(--color-app-text-secondary)]">
                        MaStR-Nr.: {item.operatorMastrNumber}
                      </p>
                    ) : null}
                    <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-xs">
                      {item.operatorProfileUrl ? (
                        <a href={item.operatorProfileUrl} target="_blank" rel="noopener noreferrer" className="text-blue-600 underline">
                          Betreiberprofil im MaStR öffnen
                        </a>
                      ) : null}
                      {item.sourceRecordUrl ? (
                        <a href={item.sourceRecordUrl} target="_blank" rel="noopener noreferrer" className="text-blue-600 underline">
                          Anlageneintrag im MaStR öffnen
                        </a>
                      ) : null}
                      {item.operatorContact?.phone ? (
                        <a href={`tel:${item.operatorContact.phone.replace(/[^\d+*#,;]/g, "")}`} className="text-blue-600 underline">
                          Telefon
                        </a>
                      ) : null}
                      {item.operatorContact?.email ? (
                        <a href={`mailto:${item.operatorContact.email.replace(/^mailto:/i, "")}`} className="text-blue-600 underline">
                          E-Mail
                        </a>
                      ) : null}
                      {item.operatorContact?.website && safeWebsiteHref(item.operatorContact.website) ? (
                        <a href={safeWebsiteHref(item.operatorContact.website)!} target="_blank" rel="noopener noreferrer" className="text-blue-600 underline">
                          Website
                        </a>
                      ) : null}
                    </div>
                    {!item.operatorProfileUrl && !item.sourceRecordUrl &&
                    !item.operatorContact?.email && !item.operatorContact?.phone &&
                    !item.operatorContact?.website ? (
                      <p className="text-xs text-[var(--color-app-text-secondary)]">
                        Keine öffentlichen Kontaktdaten in dieser Quelle hinterlegt.
                      </p>
                    ) : null}
                    {item.estimatedTipHeightM != null ? (
                      <p className="mt-1 text-xs text-[var(--color-app-text-secondary)]">
                        Geschätzte Gesamthöhe ≈ {Math.round(item.estimatedTipHeightM)} m (Nabenhöhe + halber Rotordurchmesser)
                      </p>
                    ) : null}
                    {item.taggedHeightM != null ? (
                      <p className="mt-1 text-xs text-[var(--color-app-text-secondary)]">
                        In {item.source} eingetragene Höhe: {Math.round(item.taggedHeightM)} m
                      </p>
                    ) : null}
                    {item.voltage ? (
                      <p className="mt-1 text-xs text-[var(--color-app-text-secondary)]">
                        Spannung laut {item.source}: {item.voltage}
                      </p>
                    ) : null}
                    <p className="mt-1 text-xs text-[var(--color-app-text-secondary)]">
                      Quelle: {item.displayedSource}
                    </p>
                    <p className="mt-1 text-xs text-[var(--color-app-text-secondary)]">
                      {comparisonLabel(item, data.country)}
                    </p>
                    {caution ? (
                      <p className="mt-1 text-xs text-[var(--color-caution)]">
                        {countryIso === "DE"
                          ? "Unter 100 m laut Kartendistanz: Falls die Anlage unter § 21h Abs. 3 Nr. 3 fällt, ist ausdrückliche Zustimmung der zuständigen Stelle oder des Betreibers nötig. Die gesetzliche Distanz gilt ab der Anlagenbegrenzung."
                          : "Unter 100 m zum Startpunkt: genaue Anlagenbegrenzung und örtliche Voraussetzungen prüfen."}
                      </p>
                    ) : null}
                  </article>
                );
              })}
            </div>
          )}

          {omittedCount > 0 ? (
            <p className="text-xs text-[var(--color-app-text-secondary)]">
              Weitere {omittedCount} Treffer sind in der Kurzliste nicht aufgeführt.
            </p>
          ) : null}

          {foundCount > 0 && energyRule ? (
            <div className="rounded-md border border-[var(--color-app-border)] bg-[var(--color-app-bg)] p-2">
              <p className="text-xs font-semibold text-[var(--color-app-text)]">
                § 21h Abs. 3 Nr. 3 LuftVO · {energyRule.paragraph}
              </p>
              {energyRule.tatbestand ? (
                <p className="mt-1 text-xs text-[var(--color-app-text-secondary)]">
                  {energyRule.tatbestand}
                </p>
              ) : null}
              <p className="mt-1 text-xs text-[var(--color-app-text-secondary)]">
                {energyRule.auflagen}
              </p>
            </div>
          ) : null}

          <p className="text-xs text-[var(--color-app-text-secondary)]">
            Kontaktangaben erscheinen nur, wenn die jeweilige Quelle sie öffentlich bereitstellt. Natürliche Personen können im MaStR maskiert und nur über ihre ABR-Nummer auffindbar sein.
          </p>

          {countryIso === "DE" ? (
            <p className="text-[10px] leading-4 text-[var(--color-app-text-secondary)]">
              {data.country.officialGeozoneSource.label}-Abgleich: Wind {sourceStatusLabel(data.sources.officialWind)} · Strom {sourceStatusLabel(data.sources.officialPower)}
            </p>
          ) : (
            <p className="text-[10px] leading-4 text-[var(--color-app-text-secondary)]">
              {data.country.comparisonNote}
            </p>
          )}
          {data.country.dataSources.map((source) => {
            const status = source.referenceOnly
              ? null
              : data.sources[source.statusKey ?? (source.id === "mastr" ? "mastr" : "openStreetMap")];
            return (
              <p key={source.id} className="text-[10px] leading-4 text-[var(--color-app-text-secondary)]">
                {source.label}: {status ? sourceStatusLabel(status) : "nicht eingebunden"}
                {status?.updatedAt ? ` · Stand ${new Date(status.updatedAt).toLocaleDateString("de-DE")}` : ""}
                {" · "}{source.license} · {source.coverage} · {source.updateStatus}
              </p>
            );
          })}
        </>
      ) : null}

      <p className="text-[10px] leading-4 text-[var(--color-app-text-secondary)]">
        Amtliche Anlagendatensätze und OSM-Ergänzungen sind keine Geozonenerfassung. Ein fehlender Treffer bedeutet nicht, dass keine Anlage oder Einschränkung besteht. Abstände ersetzen nicht die Prüfung der offiziellen Karte.
      </p>
      <div className="flex flex-wrap gap-x-3 gap-y-1 text-[10px] font-medium">
        {data?.country.dataSources.map((source) => (
          <a key={source.id} href={source.url} target="_blank" rel="noreferrer" className="text-brand-600 hover:underline">
            {source.label} · {source.license}
          </a>
        ))}
        <a
          href={data?.country.officialGeozoneSource.url ?? "https://www.dipul.de/"}
          target="_blank"
          rel="noreferrer"
          className="text-brand-600 hover:underline"
        >
          {data?.country.officialGeozoneSource.label ?? "Amtliche Geozonenkarte"}
        </a>
      </div>
    </section>
  );
}
